import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ESGScore, calculateOverallScore, ESG_COLORS, ESG_CHIP_BACKGROUNDS } from '../../../../shared/models/esg.model';

@Component({
  selector: 'app-esg-hero',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './esg-hero.component.html',
  styleUrls: ['./esg-hero.component.scss']
})
export class EsgHeroComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() score: ESGScore = {
    environment: 75,
    social: 70,
    governance: 70
  };

  @Input() size = 120;
  @Input() strokeWidth = 10;

  @Output() cardClick = new EventEmitter<void>();
  @Output() categoryClick = new EventEmitter<'environment' | 'social' | 'governance'>();

  @ViewChild('ringContainer') ringContainer!: ElementRef<HTMLDivElement>;

  // Animation state
  animateRing = false;
  displayedScore = 0;

  // Async operation IDs for cleanup
  private fallbackTimeoutId?: number;
  private counterDelayTimeoutId?: number;
  private animationFrameId?: number;

  // Calculated values
  overallScore = 0;
  radius = 0;
  circumference = 0;
  segmentLength = 0;

  // Colors from model
  readonly colors = ESG_COLORS;
  readonly chipBackgrounds = ESG_CHIP_BACKGROUNDS;

  ngOnInit(): void {
    this.overallScore = calculateOverallScore(this.score);
    this.calculateRingValues();
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnDestroy(): void {
    if (this.fallbackTimeoutId !== undefined) {
      clearTimeout(this.fallbackTimeoutId);
    }
    if (this.counterDelayTimeoutId !== undefined) {
      clearTimeout(this.counterDelayTimeoutId);
    }
    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  private calculateRingValues(): void {
    this.radius = (this.size / 2) - (this.strokeWidth / 2) - 8;
    this.circumference = 2 * Math.PI * this.radius;
    this.segmentLength = this.circumference / 3;
  }

  private setupIntersectionObserver(): void {
    if (!this.ringContainer?.nativeElement) {
      this.fallbackTimeoutId = window.setTimeout(() => this.startAnimation(), 100);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.animateRing) {
            this.startAnimation();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(this.ringContainer.nativeElement);
  }

  private startAnimation(): void {
    this.animateRing = true;
    this.animateScoreCounter();
  }

  private animateScoreCounter(): void {
    const duration = 1000;
    const startTime = performance.now();
    const targetScore = this.overallScore;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);
      this.displayedScore = Math.round(targetScore * easeOut);

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        this.animationFrameId = undefined;
      }
    };

    this.counterDelayTimeoutId = window.setTimeout(() => {
      this.counterDelayTimeoutId = undefined;
      this.animationFrameId = requestAnimationFrame(animate);
    }, 300);
  }

  getSegmentDasharray(scoreValue: number): string {
    const maxSegmentArc = this.segmentLength;
    const actualArc = (scoreValue / 100) * maxSegmentArc;
    return `${actualArc} ${this.circumference}`;
  }

  getInitialDasharray(): string {
    return `0 ${this.circumference}`;
  }

  getSegmentOffset(index: number): number {
    return -(index * this.segmentLength);
  }

  onCardClick(): void {
    this.cardClick.emit();
  }

  onCategoryClick(category: 'environment' | 'social' | 'governance', event: Event): void {
    event.stopPropagation();
    this.categoryClick.emit(category);
  }
}
